import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import Helpers from "../../Helpers";

export default function TestSelectModal({
    isOpen,
    onClose,
    companyId,
    userId,
    caption,
    buttonText,
    mode
}) {
    const [tests, updateTests] = useState([]);
    const [selectedTests, setSelectedTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortColumn, setSortColumn] = useState("lastName"); // Default sort column
    const [sortDirection, setSortDirection] = useState("asc"); // Default sort direction

    useEffect(() => {
        getTestsAsync();
    }, [isOpen, companyId]);

    // Handle escape key to close modal
    useEffect(() => {
        if (!isOpen) return;

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);


    function handleSort(column) {
        if (sortColumn === column) {
            // Toggle sort direction if the same column is clicked
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            // Set the new column and default to ascending order
            setSortColumn(column);
            setSortDirection("asc");
        }

        // Sort the data
        const sortedTests = [...tests].sort((a, b) => {
            const aValue = a[column]?.toString().toLowerCase() || "";
            const bValue = b[column]?.toString().toLowerCase() || "";

            if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
            if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });

        updateTests(sortedTests);
    }

    async function getTestsAsync() {
        if (companyId) {
            setLoading(true);
            const resp = await RightTimeApi.getAsync(`/Tests/${companyId}/Unassigned`);
            if (resp && resp.ok) {
                updateTests(resp.jsonData.tests.sort((a, b) => {
                    if (a.lastName.toLowerCase() < b.lastName.toLowerCase()) return -1;
                    if (a.lastName.toLowerCase() > b.lastName.toLowerCase()) return 1;
                    if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) return -1;
                    if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) return 1;
                    return 0;
                }));
            }
            setLoading(false);
        }
    }
    async function toggleRowSelection(e) {
        const testId = parseInt(e.currentTarget.getAttribute("data-id"), 10);
        const selected = selectedTests.includes(testId);
        if (selected) {
            setSelectedTests(selectedTests.filter((id) => id !== testId));
        } else {
            setSelectedTests([...selectedTests, testId]);
        }
    }
    async function associateTestsAsync() {
        switch (mode) {
            case "existingUser":
                if (companyId && selectedTests.length > 0) {
                    const apiData = { companyId: companyId, userId: userId, testIds: selectedTests };
                    updateTests([]);
                    setSelectedTests([]);
                    const resp = await RightTimeApi.postAsync(`/Tests/Assign`, apiData);
                    if (resp && resp.ok) {
                        setSelectedTests([]);
                        onClose();
                    }
                }
                break;
            case "newUser":
                selectedTests.forEach(async (testId) => {
                    await RightTimeApi.postAsync(`/CreateUserFromTest/${testId}`);
                });
                setSelectedTests([]);
                onClose();
                break;
            default:
                break;
        }
    }

    if (isOpen && !loading) {
        const footer = (
            <>
                <div className="footerLeft"></div>
                <div className="footerRight actions">
                    <button className="btn btn-primary" onClick={associateTestsAsync}>{buttonText}</button>
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                </div>
            </>
        );

        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={caption}
                className="testSelectModal"
                footer={footer}
            >
                <div className="modal-content">
                    <div className="scrollable">
                        <table className="testTable">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th onClick={() => handleSort("companyTestTypeName")}>
                                        Type {sortColumn === "companyTestTypeName" && (sortDirection === "asc" ? "▲" : "▼")}
                                    </th>
                                    <th onClick={() => handleSort("firstName")}>
                                        First Name {sortColumn === "firstName" && (sortDirection === "asc" ? "▲" : "▼")}
                                    </th>
                                    <th onClick={() => handleSort("lastName")}>
                                        Last Name {sortColumn === "lastName" && (sortDirection === "asc" ? "▲" : "▼")}
                                    </th>
                                    <th onClick={() => handleSort("finished")}>
                                        Date {sortColumn === "finished" && (sortDirection === "asc" ? "▲" : "▼")}
                                    </th>
                                </tr>
                            </thead>
                            {tests && tests.length > 0 ? tests.map((test) => (
                                <tr
                                    onClick={toggleRowSelection}
                                    key={test.id}
                                    data-id={test.id}
                                    className={selectedTests.includes(test.id) ? "selected" : ""}
                                >
                                    <td>{test.id}</td>
                                    <td>{test.companyTestTypeName}</td>
                                    <td>{test.firstName}</td>
                                    <td>{test.lastName}</td>
                                    <td>{Helpers.EST(test.finished, "MM/dd/yyyy")}</td>
                                </tr>
                            )) : null}
                        </table>
                    </div>
                </div>
            </Modal>
        );
    } else {
        return null;
    }
}

